import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container, Box, CircularProgress, Alert } from '@mui/material';
import { useSession } from './context/SessionContext';
import { usePortalAuth } from './context/PortalAuthContext';
import WelcomePage from './pages/WelcomePage';
import ExitPage from './pages/ExitPage';
import PreSurveyPage from './pages/PreSurveyPage';
import DirectionsPage from './pages/DirectionsPage';
import AssessmentPage from './pages/AssessmentPage';
import SkippedReviewPage from './pages/SkippedReviewPage';
import FeedbackTeaserPage from './pages/FeedbackTeaserPage';
import FeedbackPage from './pages/FeedbackPage';
import PostSurveyPage from './pages/PostSurveyPage';
import CompletePage from './pages/CompletePage';
import MidSemesterSurveyPage from './pages/MidSemesterSurveyPage';
import EndSemesterSurveyPage from './pages/EndSemesterSurveyPage';
import HomePage from './pages/HomePage';
import AboutUsPage from './pages/AboutUsPage';
import SignInPage from './pages/SignInPage';
import RegisterPage from './pages/RegisterPage';
import StudentPortalPage from './pages/StudentPortalPage';
import StudentClassPage from './pages/StudentClassPage';
import StudentAssessmentFeedbackPage from './pages/StudentAssessmentFeedbackPage';
import FacultyPortalPage from './pages/FacultyPortalPage';
import FacultyClassPage from './pages/FacultyClassPage';
import FacultyStudentProfilePage from './pages/FacultyStudentProfilePage';
import AdminPortalPage from './pages/AdminPortalPage';
import AccountPage from './pages/AccountPage';
import AppNavbar from './components/AppNavbar';

function SessionFlow() {
  const { session, loading, error } = useSession();

  // Update document title based on current state
  useEffect(() => {
    const titles: Record<string, string> = {
      welcome: 'Welcome - alMATHEMA',
      exit: 'Thank You - alMATHEMA',
      pre_survey: 'Pre-Assessment Survey - alMATHEMA',
      directions: 'Assessment Directions - alMATHEMA',
      assessment: 'Diagnostic Assessment - alMATHEMA',
      skipped_review: 'Review Skipped Questions - alMATHEMA',
      feedback_teaser: 'Preparing Results - alMATHEMA',
      feedback: 'Your Results - alMATHEMA',
      post_survey: 'Post-Assessment Survey - alMATHEMA',
      complete: 'Assessment Complete - alMATHEMA',
    };
    document.title = titles[session?.status || 'welcome'] || 'alMATHEMA';
  }, [session?.status]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        role="status"
        aria-label="Loading application"
      >
        <CircularProgress size={60} aria-hidden="true" />
        <span className="sr-only">Loading...</span>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" role="alert">
          {error}. Please refresh the page to try again.
        </Alert>
      </Container>
    );
  }

  const renderPage = () => {
    switch (session?.status) {
      case 'welcome':
        return <WelcomePage />;
      case 'exit':
        return <ExitPage />;
      case 'pre_survey':
        return <PreSurveyPage />;
      case 'directions':
        return <DirectionsPage />;
      case 'assessment':
        return <AssessmentPage />;
      case 'skipped_review':
        return <SkippedReviewPage />;
      case 'feedback_teaser':
        return <FeedbackTeaserPage />;
      case 'feedback':
        return <FeedbackPage />;
      case 'post_survey':
        return <PostSurveyPage />;
      case 'complete':
        return <CompletePage />;
      case 'expired':
        return <ExitPage expired />;
      default:
        return <WelcomePage />;
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        py: 4,
      }}
    >
      <a
        href="#main-content"
        className="sr-only"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        onFocus={(e) => {
          e.currentTarget.style.position = 'static';
          e.currentTarget.style.width = 'auto';
          e.currentTarget.style.height = 'auto';
        }}
        onBlur={(e) => {
          e.currentTarget.style.position = 'absolute';
          e.currentTarget.style.left = '-10000px';
          e.currentTarget.style.width = '1px';
          e.currentTarget.style.height = '1px';
        }}
      >
        Skip to main content
      </a>
      <Container maxWidth="md" id="main-content" tabIndex={-1}>
        {renderPage()}
      </Container>
    </Box>
  );
}

function ProtectedRoute({
  roles,
  children,
}: {
  roles: Array<'student' | 'educator' | 'admin'>;
  children: ReactElement;
}) {
  const { user, loading } = usePortalAuth();

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  if (!roles.includes(user.role)) {
    const fallbackPath = user.role === 'student'
      ? '/student-portal'
      : user.role === 'admin'
        ? '/admin-portal'
        : '/educator-portal';
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
}

function ProtectedAssessmentRoute({ children }: { children: ReactElement }) {
  const location = useLocation();
  const { user, loading: authLoading } = usePortalAuth();
  const { session, loading: sessionLoading, refreshSession } = useSession();
  const hasRequestedSessionRef = useRef(false);

  useEffect(() => {
    if (!user) {
      hasRequestedSessionRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!user || session || sessionLoading || hasRequestedSessionRef.current) {
      return;
    }

    hasRequestedSessionRef.current = true;
    void refreshSession();
  }, [refreshSession, session, sessionLoading, user]);

  if (authLoading || (user && !session && sessionLoading)) {
    return (
      <Box sx={{ py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  return children;
}

function App() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isLandingPage = isHome || location.pathname === '/about-us';

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const routes = (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about-us" element={<AboutUsPage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/account"
        element={(
          <ProtectedRoute roles={['student', 'educator', 'admin']}>
            <AccountPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/student-portal"
        element={(
          <ProtectedRoute roles={['student']}>
            <StudentPortalPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/student-portal/classes/:sectionId"
        element={(
          <ProtectedRoute roles={['student']}>
            <StudentClassPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/student-portal/classes/:sectionId/feedback/:sessionId"
        element={(
          <ProtectedRoute roles={['student']}>
            <StudentAssessmentFeedbackPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/educator-portal"
        element={(
          <ProtectedRoute roles={['educator', 'admin']}>
            <FacultyPortalPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/educator-portal/classes/:sectionId"
        element={(
          <ProtectedRoute roles={['educator', 'admin']}>
            <FacultyClassPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/educator-portal/classes/:sectionId/students/:studentId"
        element={(
          <ProtectedRoute roles={['educator', 'admin']}>
            <FacultyStudentProfilePage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/faculty-portal"
        element={<Navigate to="/educator-portal" replace />}
      />
      <Route
        path="/admin-portal"
        element={(
          <ProtectedRoute roles={['admin']}>
            <AdminPortalPage />
          </ProtectedRoute>
        )}
      />
      <Route path="/survey/mid" element={<MidSemesterSurveyPage />} />
      <Route path="/survey/end" element={<EndSemesterSurveyPage />} />
      <Route
        path="/assessment"
        element={(
          <ProtectedAssessmentRoute>
            <SessionFlow />
          </ProtectedAssessmentRoute>
        )}
      />
      <Route path="*" element={<SessionFlow />} />
    </Routes>
  );

  return (
    <Box>
      <AppNavbar
        mode={isLandingPage ? 'landing' : 'app'}
        onHomeClick={isHome ? scrollToTop : undefined}
        onAboutClick={isHome ? () => scrollToSection('about-us-section') : undefined}
        onContactClick={isHome ? () => scrollToSection('contact-us-section') : undefined}
      />
      {isLandingPage ? routes : <Container maxWidth="lg">{routes}</Container>}
    </Box>
  );
}

export default App;
